import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Clock, Tag, Store, ArrowLeft, Star, CalendarCheck, Ticket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { StarRating } from '../components/StarRating';
import { ReviewCard } from '../components/ReviewCard';
import { ReviewForm } from '../components/ReviewForm';
import { CreateReservationModal } from '../components/ReservationSystem';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { pwaAnalytics } from '../utils/pwaAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const KuponDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [kupon, setKupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [userReservation, setUserReservation] = useState(null);
  
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    fetchKupon();
    fetchReviews();
    fetchReviewStats();
    if (user) {
      checkCanReview();
      checkUserReservation();
    }
  }, [id]);

  const fetchKupon = async () => {
    try {
      const response = await axios.get(`${API}/kupons/${id}`);
      setKupon(response.data);
      // Track kupon view
      pwaAnalytics.instance?.trackKuponView(id, response.data.title);
    } catch (error) {
      console.error('Error fetching kupon:', error);
      toast.error('Error al cargar kupon');
    } finally {
      setLoading(false);
    }
  };

  const checkUserReservation = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/reservations/user`, {
        headers: { 'X-User-Id': user.id }
      });
      const existing = response.data.find(
        r => r.kupon_id === id && ['pending', 'verification_pending'].includes(r.status)
      );
      setUserReservation(existing);
    } catch (error) {
      console.error('Error checking reservation:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/kupon/${id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchReviewStats = async () => {
    try {
      const response = await axios.get(`${API}/reviews/stats/kupon/${id}`);
      setReviewStats(response.data);
    } catch (error) {
      console.error('Error fetching review stats:', error);
    }
  };

  const checkCanReview = async () => {
    try {
      const response = await axios.get(`${API}/reviews/user/${user.id}/can-review/${id}`);
      setCanReview(response.data.canReview);
    } catch (error) {
      console.error('Error checking can review:', error);
    }
  };

  const handleReviewSubmitted = () => {
    fetchReviews();
    fetchReviewStats();
    setCanReview(false);
  };

  const handleReservationSuccess = (reservation) => {
    setShowReservationModal(false);
    setUserReservation(reservation);
    toast.success(t('reservation.success_message') || '¡Reserva confirmada!');
    // Refresh kupon to get updated stock
    fetchKupon();
  };

  // Countdown state - must be before any conditional returns
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0, expired: false });
  
  // Countdown effect
  useEffect(() => {
    if (!kupon?.expiresAt) return;
    
    const expiresAt = new Date(kupon.expiresAt);
    
    const calculateTimeLeft = () => {
      const now = new Date();
      const diff = expiresAt - now;
      
      if (diff <= 0) {
        return { hours: 0, minutes: 0, seconds: 0, expired: true };
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      return { hours, minutes, seconds, expired: false };
    };
    
    setTimeLeft(calculateTimeLeft());
    
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    return () => clearInterval(timer);
  }, [kupon?.expiresAt]);
  
  const formatCountdown = () => {
    if (timeLeft.expired) return 'Expirado';
    const pad = (n) => n.toString().padStart(2, '0');
    return `${pad(timeLeft.hours)}:${pad(timeLeft.minutes)}:${pad(timeLeft.seconds)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-local-sage" />
      </div>
    );
  }

  if (!kupon) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Kupon no enkontrado</p>
      </div>
    );
  }

  const discount = Math.round(((kupon.originalPrice - kupon.discountedPrice) / kupon.originalPrice) * 100);

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/kupons')}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Volver
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-card bg-gradient-to-br from-local-sage to-local-sage/60">
              {kupon.imageUrl ? (
                <img src={kupon.imageUrl} alt={kupon.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Tag className="w-24 h-24 text-white/50" />
                </div>
              )}
              <div className="absolute top-6 right-6 bg-reward-gold text-white px-6 py-3 rounded-full font-bold text-xl">
                -{discount}%
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Store className="w-4 h-4" />
              <span>{kupon.businessName}</span>
            </div>

            <h1 className="font-manrope font-bold text-4xl mb-4">{kupon.title}</h1>

            {/* Rating Summary */}
            {reviewStats && reviewStats.totalReviews > 0 && (
              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <StarRating rating={reviewStats.averageRating} size="md" />
                  <span className="font-semibold text-lg">{reviewStats.averageRating}</span>
                </div>
                <span className="text-muted-foreground">
                  ({reviewStats.totalReviews} {reviewStats.totalReviews === 1 ? 'review' : 'reviews'})
                </span>
              </div>
            )}

            <div className="flex items-end gap-3 mb-6">
              <span className="text-5xl font-bold text-local-sage">
                {kupon.discountedPrice.toFixed(2)}€
              </span>
              <span className="text-xl text-muted-foreground line-through mb-2">
                {kupon.originalPrice.toFixed(2)}€
              </span>
            </div>

            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              {kupon.description}
            </p>

            {/* Meta Info */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className={`rounded-xl p-4 ${timeLeft.expired ? 'bg-red-50 dark:bg-red-900/20' : timeLeft.hours < 2 ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-muted/50'}`}>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Clock className={`w-4 h-4 ${timeLeft.expired ? 'text-red-500' : timeLeft.hours < 2 ? 'text-amber-500' : ''}`} />
                  <span>Tiempo restante</span>
                </div>
                <p className={`font-mono font-bold text-2xl ${timeLeft.expired ? 'text-red-500' : timeLeft.hours < 2 ? 'text-amber-500' : ''}`}>
                  {formatCountdown()}
                </p>
              </div>
              <div className="bg-muted/50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Tag className="w-4 h-4" />
                  <span>Kategoría</span>
                </div>
                <p className="font-semibold text-lg">{kupon.category}</p>
              </div>
            </div>

            {/* Reservation Info or Button */}
            {userReservation ? (
              <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/30 rounded-2xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <CalendarCheck className="w-6 h-6 text-emerald-600" />
                  <h3 className="font-semibold text-lg text-emerald-800 dark:text-emerald-200">
                    {t('reservation.already_reserved') || 'Ya tienes una reserva'}
                  </h3>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4">
                  <p className="text-xs text-gray-500 mb-1">{t('reservation.code') || 'Código de reserva'}</p>
                  <p className="font-mono text-2xl font-bold text-gray-900 dark:text-white">
                    {userReservation.id.slice(0, 8).toUpperCase()}
                  </p>
                </div>
                <div className="text-sm text-emerald-700 dark:text-emerald-300 space-y-2">
                  <p className="flex items-center gap-2">
                    <Store className="w-4 h-4" />
                    {t('reservation.show_code') || 'Muestra este código en el negocio'}
                  </p>
                  <p className="flex items-center gap-2">
                    <Ticket className="w-4 h-4" />
                    {t('reservation.upload_later') || 'Después de pagar, sube tu ticket para ganar puntos'}
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/profile')}
                  className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="view-reservations-btn"
                >
                  {t('reservation.view_my_reservations') || 'Ver mis reservas'}
                </Button>
              </div>
            ) : (
              <>
                {/* How it works info */}
                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/30 rounded-2xl p-6 mb-6">
                  <h3 className="font-semibold text-lg mb-4 text-blue-800 dark:text-blue-200">
                    {t('reservation.how_it_works') || '¿Cómo funciona?'}
                  </h3>
                  <div className="space-y-3 text-sm text-blue-700 dark:text-blue-300">
                    <div className="flex items-start gap-3">
                      <span className="bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                      <p>{t('reservation.step_1') || 'Reserva gratis desde aquí'}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                      <p>{t('reservation.step_2') || 'Ve al negocio y muestra tu código'}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                      <p>{t('reservation.step_3') || 'Paga directamente en el local'}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                      <p>{t('reservation.step_4') || 'Sube tu ticket y gana puntos'}</p>
                    </div>
                  </div>
                </div>

                {/* Points Info */}
                <div className="bg-local-sage/10 border border-local-sage/20 rounded-2xl p-4 mb-4">
                  <p className="text-sm text-local-sage font-medium">
                    🌟 {t('reservation.points_info', { points: Math.floor(kupon.discountedPrice) }) || `Ganarás ${Math.floor(kupon.discountedPrice)} puntos al verificar tu ticket`}
                  </p>
                </div>

                {/* Reserve Button */}
                {user ? (
                  <Button
                    onClick={() => setShowReservationModal(true)}
                    disabled={kupon.stock <= 0}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-full py-6 text-lg"
                    data-testid="reserve-button"
                  >
                    <CalendarCheck className="w-5 h-5 mr-2" />
                    {kupon.stock > 0 
                      ? (t('reservation.reserve_free') || 'Reservar Gratis') 
                      : (t('kupon.sold_out') || 'Agotado')
                    }
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-full py-6 text-lg"
                    data-testid="login-to-reserve-btn"
                  >
                    {t('reservation.login_to_reserve') || 'Inicia sesión para reservar'}
                  </Button>
                )}

                {kupon.stock <= 5 && kupon.stock > 0 && (
                  <p className="text-sm text-center text-muted-foreground mt-4">
                    {t('kupon.limited_stock', { count: kupon.stock }) || `¡Solo quedan ${kupon.stock} disponibles!`}
                  </p>
                )}
              </>
            )}

            {/* Reservation Modal */}
            <AnimatePresence>
              {showReservationModal && kupon && (
                <CreateReservationModal
                  kupon={kupon}
                  onClose={() => setShowReservationModal(false)}
                  onSuccess={handleReservationSuccess}
                />
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Reviews Section */}
        <div className="mt-16">
          <h2 className="font-manrope font-bold text-3xl mb-8">Reviews de Klientes</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Review Form */}
            {user && canReview && (
              <div className="lg:col-span-3">
                <ReviewForm kuponId={id} onReviewSubmitted={handleReviewSubmitted} />
              </div>
            )}

            {/* Review Stats */}
            {reviewStats && reviewStats.totalReviews > 0 && (
              <div className="bg-card rounded-2xl p-6 border border-border/50">
                <h3 className="font-semibold text-lg mb-4">Resumen de Kalifikaciones</h3>
                <div className="flex items-center gap-4 mb-6">
                  <div className="text-5xl font-bold text-local-sage">{reviewStats.averageRating}</div>
                  <div>
                    <StarRating rating={reviewStats.averageRating} size="md" />
                    <p className="text-sm text-muted-foreground mt-1">
                      {reviewStats.totalReviews} {reviewStats.totalReviews === 1 ? 'review' : 'reviews'}
                    </p>
                  </div>
                </div>
                
                {/* Rating Distribution */}
                <div className="space-y-2">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = reviewStats.ratingDistribution[star.toString()] || 0;
                    const percentage = reviewStats.totalReviews > 0 ? (count / reviewStats.totalReviews) * 100 : 0;
                    
                    return (
                      <div key={star} className="flex items-center gap-2">
                        <span className="text-sm w-3">{star}</span>
                        <Star className="w-4 h-4 fill-reward-gold text-reward-gold" />
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-reward-gold"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground w-8">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Reviews List */}
            <div className={reviewStats && reviewStats.totalReviews > 0 ? "lg:col-span-2" : "lg:col-span-3"}>
              {reviews.length > 0 ? (
                <div className="space-y-4" data-testid="reviews-list">
                  {reviews.map((review) => (
                    <ReviewCard key={review.id} review={review} />
                  ))}
                </div>
              ) : (
                <div className="bg-card rounded-2xl p-12 text-center border border-border/50">
                  <Star className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    Aún no hay reviews para este kupon. {canReview && '¡Sé el primero en dejar una!'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};